/**
 * Markdown Shortcuts Composable
 * Handles markdown auto-conversion in Trix editor
 * Supports: **bold**, *italic*, ~~strike~~, `code`, - lists, 1. lists, > quotes, # headings
 */

import { MARKDOWN_SHORTCUTS } from './editorConfig';

// Inline closing characters that trigger pattern matching
const INLINE_TRIGGER_CHARS = ['*', '_', '~', '`'];

/**
 * Initialize markdown shortcuts for a Trix editor
 * @param {HTMLElement} editorElement - The trix-editor element
 * @returns {Function} Cleanup function to remove event listeners
 */
export function useMarkdownShortcuts(editorElement) {
  if (!editorElement || !editorElement.editor) {
    console.warn('useMarkdownShortcuts: Invalid editor element');
    return () => {};
  }

  const editor = editorElement.editor;
  let isProcessing = false;

  function handleKeydown(event) {
    // Prevent re-entry
    if (isProcessing) return;

    // Check for block-level shortcuts on space or enter
    if (event.key === ' ' || event.key === 'Enter') {
      const handled = processBlockShortcuts(event);
      if (handled) return;
    }
  }

  function handleKeyup(event) {
    // Prevent re-entry
    if (isProcessing) return;

    // Check for inline shortcuts only when typing closing characters
    if (INLINE_TRIGGER_CHARS.includes(event.key)) {
      // Small delay to let Trix finish processing the keystroke
      setTimeout(() => {
        if (!isProcessing) {
          processInlineShortcuts();
        }
      }, 10);
    }
  }

  function processBlockShortcuts(event) {
    const document = editor.getDocument();
    const position = editor.getPosition();

    // Get the current piece of text before cursor
    const text = document.toString();
    const textBeforeCursor = text.substring(0, position);

    // Find the start of the current line
    const lineStart = textBeforeCursor.lastIndexOf('\n') + 1;
    const currentLineText = textBeforeCursor.substring(lineStart);

    // Check for block-level shortcuts (lists, quotes, headings)
    for (const shortcut of MARKDOWN_SHORTCUTS) {
      if (shortcut.blockAttribute && shortcut.pattern.test(currentLineText)) {
        event.preventDefault();
        isProcessing = true;

        try {
          // Calculate the range to delete the markdown syntax
          const matchLength = currentLineText.length;
          const startPosition = position - matchLength;

          // Delete the markdown syntax
          editor.setSelectedRange([startPosition, position]);
          editor.deleteInDirection('forward');

          // Apply the block attribute
          editor.activateAttribute(shortcut.blockAttribute);
        } finally {
          isProcessing = false;
        }

        return true;
      }
    }
    return false;
  }

  function processInlineShortcuts() {
    if (isProcessing) return;

    const document = editor.getDocument();
    const position = editor.getPosition();

    if (position === 0) return;

    const text = document.toString();
    const textBeforeCursor = text.substring(0, position);

    // Check each inline shortcut pattern
    for (const shortcut of MARKDOWN_SHORTCUTS) {
      // Skip block attributes
      if (shortcut.blockAttribute) continue;

      const match = textBeforeCursor.match(shortcut.pattern);
      if (match) {
        const fullMatch = match[0];
        const capturedText = match[1];

        if (!capturedText || capturedText.trim() === '') continue;

        isProcessing = true;

        try {
          // Calculate positions
          const matchStart = position - fullMatch.length;

          // Delete the markdown syntax
          editor.setSelectedRange([matchStart, position]);
          editor.deleteInDirection('forward');

          // Insert the text with formatting
          editor.insertString(capturedText);

          // Select the inserted text and apply attribute
          const newPosition = editor.getPosition();
          editor.setSelectedRange([newPosition - capturedText.length, newPosition]);
          editor.activateAttribute(shortcut.attribute);

          // Deselect and move cursor to end
          editor.setSelectedRange([newPosition, newPosition]);
          editor.deactivateAttribute(shortcut.attribute);
        } finally {
          isProcessing = false;
        }

        return;
      }
    }
  }

  // Attach event listeners - use keydown for blocks, keyup for inline
  editorElement.addEventListener('keydown', handleKeydown);
  editorElement.addEventListener('keyup', handleKeyup);

  // Return cleanup function
  return function cleanup() {
    editorElement.removeEventListener('keydown', handleKeydown);
    editorElement.removeEventListener('keyup', handleKeyup);
  };
}

/**
 * Check if text matches any markdown pattern
 * @param {string} text - Text to check
 * @returns {Object|null} Matching shortcut or null
 */
export function matchMarkdownPattern(text) {
  for (const shortcut of MARKDOWN_SHORTCUTS) {
    if (shortcut.pattern.test(text)) {
      return shortcut;
    }
  }
  return null;
}

export default useMarkdownShortcuts;
